import type React from "react";
import { Edit2, Trash2, Copy, Save, FileX } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import type { Page } from "@/store/zustand/pagesStore";

interface PageItemContextMenuProps {
  children: React.ReactNode;
  page: Page;
  onRename: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onPersist: () => void;
  onUnpersist: () => void;
  disableDelete?: boolean;
}

export const PageItemContextMenu: React.FC<PageItemContextMenuProps> = ({
  children,
  page,
  onRename,
  onDelete,
  onDuplicate,
  onPersist,
  onUnpersist,
  disableDelete,
}) => {
  const isInTempobook = page.configPath?.includes("tempobook/");
  const isInTempo = page.configPath?.includes("tempo/");

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
        className="bg-background border-border text-text-primary"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <ContextMenuItem
          className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
          onClick={(e) => {
            e.stopPropagation();
            // Add small delay to let context menu fully close before starting rename
            setTimeout(() => onRename(), 200);
          }}
        >
          <Edit2 className="w-3 h-3 mr-2" />
          Rename Page
        </ContextMenuItem>

        <ContextMenuItem
          className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
        >
          <Copy className="w-3 h-3 mr-2" />
          Duplicate Page
        </ContextMenuItem>

        <ContextMenuItem
          className={`text-xs ${
            disableDelete
              ? "text-gray-500 cursor-default focus:bg-transparent hover:bg-transparent focus:text-gray-500 hover:text-gray-500"
              : "text-destructive hover:bg-[#2f3035] focus:bg-[#2f3035]"
          }`}
          data-tooltip-id={disableDelete ? "tooltip" : undefined}
          data-tooltip-content={
            disableDelete ? "Canvases must have at least one page." : undefined
          }
          data-tooltip-place="right"
          onClick={(e) => {
            e.stopPropagation();
            if (!disableDelete) {
              onDelete();
            }
          }}
        >
          <Trash2 className="w-3 h-3 mr-2" />
          Delete Page
        </ContextMenuItem>

        <ContextMenuSeparator className="bg-[#2f3035]" />

        {isInTempobook && (
          <ContextMenuItem
            className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
            onClick={(e) => {
              e.stopPropagation();
              onPersist();
            }}
          >
            <Save className="w-3 h-3 mr-2" />
            Save to Codebase
          </ContextMenuItem>
        )}

        {isInTempo && (
          <ContextMenuItem
            className="text-xs hover:bg-[#2f3035] focus:bg-[#2f3035]"
            onClick={(e) => {
              e.stopPropagation();
              onUnpersist();
            }}
          >
            <FileX className="w-3 h-3 mr-2" />
            Remove from Codebase
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};
